/**
 * 详情页路由参数解析（query 形态与 path 形态的单一真相源）
 *
 * 三类房产详情页同时支持两种 URL 形态：
 *   path 正典： /lettingDetail/1142、/houseDetail/107?type=2、/managementDetail/11
 *   query 兜底：/lettingDetail?id=1142、/houseDetail?id=107&type=2、/managementDetail?id=11
 *
 * 页面、混入与 SEO 工具一律通过本模块读取 id / type，避免两种形态出现行为差异。
 */

/** 支持双形态的详情页基础路径 */
export const DETAIL_BASE_PATHS = ['/lettingDetail', '/houseDetail', '/managementDetail']

/** 详情页对应的列表入口（空 id / 非法 id 的前端兜底去处） */
export const DETAIL_LIST_PATHS = {
  '/lettingDetail': '/letting',
  '/houseDetail': '/sale',
  '/managementDetail': '/managementList',
}

/** 详情 id 长度上限：防御超长字符串进入接口与路径 */
export const DETAIL_ID_MAX_LENGTH = 20

/**
 * 销售详情缺省房源类型（与后端 `type` 契约一致：1=新房，2=二手房）
 * 与原先 SaleDetailPage.getNormalizedHouseType 的缺省值保持一致
 */
export const DEFAULT_HOUSE_TYPE = '2'

/**
 * 归一化路由路径：去掉 query、hash 与尾部斜杠
 * @param {string} [path]
 */
export function normalizeRoutePath (path = '/') {
  if (!path) return '/'
  const cleanPath = String(path).split('?')[0].split('#')[0]
  if (cleanPath === '/') return '/'
  return cleanPath.endsWith('/') ? cleanPath.slice(0, -1) : cleanPath
}

/**
 * 取详情页基础路径
 * `/lettingDetail/1142` → `/lettingDetail`；非详情页返回 `''`
 * @param {string} [path]
 */
export function getDetailBasePath (path = '/') {
  const cleanPath = normalizeRoutePath(path)
  return DETAIL_BASE_PATHS.find(
    (base) => cleanPath === base || cleanPath.startsWith(`${base}/`)
  ) || ''
}

/**
 * 取 path 形态的房源 ID
 * `/lettingDetail/1142` → `1142`；无 ID 返回 `''`
 * @param {string} [path]
 */
export function getDetailIdFromPath (path = '/') {
  const cleanPath = normalizeRoutePath(path)
  const base = getDetailBasePath(cleanPath)
  if (!base) return ''
  const rest = cleanPath.slice(base.length).replace(/^\//, '')
  if (!rest) return ''
  return rest.split('/')[0] || ''
}

/**
 * 归一化 id 取值：只接受标量，数组（`?id=1&id=2`）与对象一律视为非法
 * @param {any} [value]
 * @returns {string}
 */
export function toDetailIdString (value) {
  if (value === undefined || value === null) return ''
  if (typeof value === 'object') return ''
  return String(value).trim()
}

/**
 * 详情 id 是否可作为接口参数与路径段使用：有界正整数
 * @param {any} [value]
 */
export function isValidDetailId (value) {
  const id = toDetailIdString(value)
  if (!id || id.length > DETAIL_ID_MAX_LENGTH) return false
  return /^[1-9][0-9]*$/.test(id)
}

/**
 * 解析详情页参数：`params` 优先、`query` 回退
 * @param {import('vue-router').Route | { path?: string, query?: any, params?: any }} [route]
 * @returns {{ id: string, type: string, basePath: string }}
 */
export function resolveDetailParams (route = {}) {
  const path = route.path || '/'
  const query = route.query || {}
  const params = route.params || {}

  const paramId = toDetailIdString(params.id)
  const queryId = toDetailIdString(query.id)

  const id = paramId || getDetailIdFromPath(path) || queryId
  const type = query.type !== undefined && query.type !== null && query.type !== ''
    ? String(query.type)
    : ''

  return { id, type, basePath: getDetailBasePath(path) }
}

/**
 * 详情页空 id / 非法 id 的列表入口
 * `''` 表示没有可靠去处（调用方不应拼路径）
 * @param {string} [basePath] 形如 `/houseDetail`
 * @param {any} [type] 销售详情的房源类型
 */
export function getDetailListPath (basePath, type) {
  const listPath = DETAIL_LIST_PATHS[basePath]
  if (!listPath) return ''
  if (basePath === '/houseDetail') {
    return `${listPath}?type=${normalizeHouseType(type)}`
  }
  return listPath
}

/**
 * 归一化销售房源类型：仅 `1` 视为新房，其余一律二手房
 * @param {any} [type]
 */
export function normalizeHouseType (type) {
  return String(type) === '1' ? '1' : DEFAULT_HOUSE_TYPE
}

/**
 * 构造详情页 path 正典 URL
 * @param {string} basePath 形如 `/lettingDetail`
 * @param {string|number} id
 * @param {Record<string, any>} [extraQuery] 需要保留的查询参数（如 houseDetail 的 type）
 */
export function buildDetailPath (basePath, id, extraQuery = {}) {
  const idString = toDetailIdString(id)
  // 不把 undefined / null / 数组 / 对象拼成 "undefined" 之类的坏路径
  if (!idString) return ''
  const safeId = encodeURIComponent(idString)
  const search = Object.keys(extraQuery)
    .filter((key) => extraQuery[key] !== undefined && extraQuery[key] !== null && extraQuery[key] !== '')
    .map((key) => `${key}=${encodeURIComponent(String(extraQuery[key]))}`)
    .join('&')
  return search ? `${basePath}/${safeId}?${search}` : `${basePath}/${safeId}`
}
